import { cn } from "@/utils/helpers";
import {
  CALENDAR_PLATFORMS,
  type CalendarPlatform,
} from "@/types/contentCalendar.type";
import PlatformGlyph, { platformLabel } from "@/app/routes/dashboard/components/PlatformGlyph";
import type { ComponentType } from "react";
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import {
  PinterestIcon,
  RedditIcon,
  SnapchatIcon,
  SubstackIcon,
  TikTokIcon,
} from "@/app/routes/dashboard/components/socialGlyphs";

interface PlatformMeta {
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Brand-ish accent for the icon (matches the social Connections panel). */
  color: string;
}

/**
 * The coloured line icons, kept for the strategy deck and the scheduling panel.
 * The calendar's own surfaces draw PlatformGlyph instead: one official mark per
 * platform, in ink, the same in the grid, the list and the post.
 */
export const PLATFORM_META: Record<CalendarPlatform, PlatformMeta> = {
  TIKTOK: { label: "TikTok", icon: TikTokIcon, color: "text-[#25f4ee]" },
  INSTAGRAM: { label: "Instagram", icon: Instagram, color: "text-[#d96bb0]" },
  YOUTUBE: { label: "YouTube", icon: Youtube, color: "text-[#ef5b5b]" },
  FACEBOOK: { label: "Facebook", icon: Facebook, color: "text-[#5b8def]" },
  SNAPCHAT: { label: "Snapchat", icon: SnapchatIcon, color: "text-[#d0a520]" },
  LINKEDIN: { label: "LinkedIn", icon: Linkedin, color: "text-[#4a90d9]" },
  PINTEREST: { label: "Pinterest", icon: PinterestIcon, color: "text-[#d9576b]" },
  REDDIT: { label: "Reddit", icon: RedditIcon, color: "text-[#f2703c]" },
  SUBSTACK: { label: "Substack", icon: SubstackIcon, color: "text-[#e8a44c]" },
};

/** Toggle-capsule multi-select for a slot's platforms. Zero selected is allowed. */
export function PlatformPicker({
  value,
  onChange,
  className,
}: {
  value: CalendarPlatform[];
  onChange: (next: CalendarPlatform[]) => void;
  className?: string;
}) {
  const selected = new Set(value);
  const toggle = (p: CalendarPlatform) => {
    const next = new Set(selected);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    // Preserve canonical order regardless of click order.
    onChange(CALENDAR_PLATFORMS.filter((c) => next.has(c)));
  };

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {CALENDAR_PLATFORMS.map((p) => {
        const on = selected.has(p);
        return (
          <button
            key={p}
            type="button"
            role="switch"
            aria-checked={on}
            onClick={() => toggle(p)}
            className={cn(
              // Filter chip (style B): a 32px capsule; on takes the accent tint and its border.
              "inline-flex h-8 items-center gap-1.5 rounded-pill px-3 text-[13px] font-medium outline-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
              on
                ? "border border-co-accent-border bg-co-accent-dim text-co-text"
                : "co-glass-control text-co-text2 hover:text-co-text",
            )}
          >
            <PlatformGlyph platform={p} className="size-3.5" />
            {platformLabel(p)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compact read-only row of a slot's platforms: the official marks, in the
 * canonical order, in ink. The same PlatformGlyph the month grid and list draw,
 * so a platform looks like one thing across the whole calendar.
 */
export function PlatformBadges({
  platforms,
  className,
  iconClassName,
}: {
  platforms: CalendarPlatform[];
  className?: string;
  iconClassName?: string;
}) {
  if (platforms.length === 0) return null;
  const ordered = CALENDAR_PLATFORMS.filter((p) => platforms.includes(p));
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-co-text2", className)}>
      {ordered.map((p) => (
        <span key={p} className="inline-flex items-center">
          <PlatformGlyph platform={p} className={cn("size-3.5", iconClassName)} />
          <span className="sr-only">{platformLabel(p)}</span>
        </span>
      ))}
    </span>
  );
}
